/**
 * The determinations of whether a purchaser who is not accredited can, alone, judge the merits and
 * risks of an investment (17 CFR 230.506(b)(2)(ii); S-30). A manager reads the answers and decides;
 * nobody decides their own, and there is no purchaser representative: a person who does not show the
 * experience cannot buy. Reading the answers is recorded.
 */
import { useCallback, useEffect, useState } from 'react';
import regDStaffService, {
  type AnswersForReview,
  type PendingDetermination,
} from '../../services/regDStaff.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDateTime } from '../../lib/datetime';
import { colors } from '../../styles/theme';

const inputStyle = { border: `1px solid ${colors.border}` };

function Review({
  item,
  onDone,
  onError,
}: Readonly<{
  item: PendingDetermination;
  onDone: () => void;
  onError: (message: string) => void;
}>): JSX.Element {
  const [reading, setReading] = useState<AnswersForReview | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const read = async (): Promise<void> => {
    setBusy(true);
    try {
      setReading(await regDStaffService.readAnswers(item.answers_id));
    } catch (e: unknown) {
      onError(getApiErrorMessage(e, 'Could not open the answers.'));
    } finally {
      setBusy(false);
    }
  };
  const decide = async (outcome: 'qualified' | 'not_qualified'): Promise<void> => {
    setBusy(true);
    try {
      await regDStaffService.determine(item.answers_id, outcome, reason.trim());
      onDone();
    } catch (e: unknown) {
      onError(getApiErrorMessage(e, 'Could not record the determination.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="rounded-xl p-4" style={{ border: `1px solid ${colors.border}` }}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm" style={{ color: colors.ink }}>
          {item.client_name}
          <span className="text-xs ml-2" style={{ color: colors.muted }}>
            {`answers sent ${formatDateTime(item.submitted_at)}`}
          </span>
        </div>
        {reading === null && (
          <button type="button" disabled={busy} onClick={() => { void read(); }}
                  className="text-xs px-3 py-1.5 rounded-md font-semibold disabled:opacity-50"
                  style={{ border: `1px solid ${colors.border}`, color: colors.ink }}>
            Read the answers
          </button>
        )}
      </div>
      {reading !== null && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: colors.border }}>
          <dl className="space-y-3 mb-4">
            {reading.questionnaire.items.map((q) => (
              <div key={q.key}>
                <dt className="text-xs font-semibold" style={{ color: colors.inkSoft }}>{q.text}</dt>
                <dd className="text-sm" style={{ color: colors.ink }}>
                  {reading.answers[q.key] ?? '—'}
                </dd>
              </div>
            ))}
          </dl>
          <label htmlFor={`reason-${item.answers_id}`} className="block text-xs font-semibold mb-1"
                 style={{ color: colors.inkSoft }}>
            Why (at least ten characters)
          </label>
          <textarea
            id={`reason-${item.answers_id}`}
            value={reason}
            rows={2}
            maxLength={1000}
            disabled={busy}
            onChange={(e) => setReason(e.target.value)}
            className="w-full px-3 py-2 rounded-lg text-sm mb-3"
            style={inputStyle}
          />
          <div className="flex gap-2">
            <button type="button" disabled={busy || reason.trim().length < 10}
                    onClick={() => { void decide('qualified'); }}
                    className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                    style={{ background: colors.navy, color: '#FFFFFF' }}>
              Can judge it alone
            </button>
            <button type="button" disabled={busy || reason.trim().length < 10}
                    onClick={() => { void decide('not_qualified'); }}
                    className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                    style={{ border: `1px solid ${colors.border}`, color: colors.ink }}>
              Cannot
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

export default function SophisticationPanel(): JSX.Element {
  const [items, setItems] = useState<PendingDetermination[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setItems(await regDStaffService.listDeterminations());
      setError(null);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load the answers waiting for review.'));
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <section className="rounded-2xl p-5 mb-6"
             style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
      <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>
        Experience of purchasers who are not accredited
      </h2>
      <p className="text-sm mb-4" style={{ color: colors.muted }}>
        The person must be able, alone, to judge the merits and risks of the investment. If they
        cannot, they cannot buy. You never review your own answers.
      </p>
      {error && (
        <div className="mb-4 p-3 rounded-lg text-sm" role="alert"
             style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}>
          {error}
        </div>
      )}
      {items === null && error === null && (
        <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>
      )}
      {items?.length === 0 && (
        <p className="text-sm" style={{ color: colors.muted }}>No answers are waiting for review.</p>
      )}
      <ul className="space-y-3">
        {items?.map((i) => (
          <Review key={i.answers_id} item={i} onDone={() => { void reload(); }} onError={setError} />
        ))}
      </ul>
    </section>
  );
}
